/**
 * Layout surfaces of the merchant wireframe: the card, the eyebrow, the page
 * head, the definition list, the 9:16 poster placeholder, the live-work tide
 * and the decorative pixel cluster. Re-exported through `./primitives.js`.
 */

import type { CSSProperties, ReactNode } from 'react';

import { PlayIcon, WarningIcon } from './icons.js';

/* ---- Layout ---------------------------------------------------------- */

export function Card({
  children,
  className = '',
  padding,
  as: Tag = 'div',
  ...rest
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly padding?: 0 | 20 | 24;
  readonly as?: 'div' | 'section' | 'article' | 'aside';
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
  readonly 'aria-live'?: 'polite' | 'off';
  readonly role?: string;
  readonly id?: string;
  readonly style?: CSSProperties;
}): ReactNode {
  return (
    <Tag
      className={`mj-ws-card${padding === 0 ? ' is-flush' : padding === 24 ? ' is-roomy' : ''} ${className}`.trim()}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({
  children,
  className = '',
  id,
  style,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly id?: string;
  readonly style?: CSSProperties;
}): ReactNode {
  return (
    <div id={id} className={`mj-ws-eyebrow ${className}`.trim()} style={style}>
      {children}
    </div>
  );
}

export function PageHead({
  eyebrow,
  title,
  titleId,
  intro,
  action,
}: {
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  readonly titleId: string;
  readonly intro?: ReactNode;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <div className="mj-ws-phead">
      <div className="mj-ws-phead-copy">
        {eyebrow !== undefined ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <h1 id={titleId} className="mj-ws-h1">
          {title}
        </h1>
        {intro !== undefined ? (
          <p className="mj-ws-muted mj-ws-intro">{intro}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function DefinitionList({
  rows,
  columns = 'default',
  className = '',
}: {
  readonly rows: readonly (readonly [ReactNode, ReactNode] | null)[];
  readonly columns?: 'default' | 'narrow' | 'wide';
  readonly className?: string;
}): ReactNode {
  return (
    <dl className={`mj-ws-dl is-${columns} ${className}`.trim()}>
      {rows.map((row, index) =>
        row === null ? null : (
          <div key={String(index)} className="mj-ws-dl-row">
            <dt>{row[0]}</dt>
            <dd>{row[1]}</dd>
          </div>
        ),
      )}
    </dl>
  );
}

/** The 9:16 placeholder poster, with the generating grid or the play mark. */
export function Poster({
  width,
  state = 'playable',
  duration,
  label,
  posterUrl,
  mini = false,
  onClick,
  ariaLabel,
}: {
  readonly width?: number;
  readonly state?: 'playable' | 'generating' | 'failed' | 'placeholder';
  readonly duration?: string | null;
  readonly label?: ReactNode;
  readonly posterUrl?: string | null;
  readonly mini?: boolean;
  readonly onClick?: () => void;
  readonly ariaLabel?: string;
}): ReactNode {
  const body = (
    <>
      {posterUrl ? <img src={posterUrl} alt="" className="mj-ws-v916-poster" /> : null}
      {state === 'generating' ? (
        <span className="mj-ws-tgrid" aria-hidden="true">
          {Array.from({ length: 9 }, (_, index) => (
            <i key={index} />
          ))}
        </span>
      ) : state === 'failed' ? (
        <WarningIcon size={28} />
      ) : state === 'playable' ? (
        <span className="mj-ws-play" aria-hidden="true">
          <PlayIcon />
        </span>
      ) : null}
      {label !== undefined ? <span>{label}</span> : null}
      {duration ? <span className="mj-ws-dur mj-ws-tnum">{duration}</span> : null}
    </>
  );
  const className = `mj-ws-v916${mini ? ' is-mini' : ''}`;
  const style = width === undefined ? undefined : { width };
  if (onClick !== undefined) {
    return (
      <button
        type="button"
        className={className}
        style={style}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {body}
      </button>
    );
  }
  return (
    <div
      className={className}
      style={style}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      {body}
    </div>
  );
}

/** The five-bar "tide" that marks live work. */
export function Tide(): ReactNode {
  return (
    <span className="mj-ws-tide" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

/** The wireframe's decorative pixel cluster for empty states. */
export function PixelCluster(): ReactNode {
  return (
    <span className="mj-ws-pxl" aria-hidden="true">
      {Array.from({ length: 12 }, (_, index) => (
        <i key={index} />
      ))}
    </span>
  );
}
